import { DateTime } from "luxon";

import i18n from "../../i18n";
import { PatternStatus } from "./PatternLifecycleDTO";
import { MaterialUnit, StorageClass } from "./ExplosivesDTO";
import type { Warning } from "../../i18n/serverMessages";

export enum HoleStatus {
  Designed = 0,
  Drilled = 1,
  RedrillRequired = 2,
  BackfillRequired = 3,
  Dipped = 4,
  Charged = 5,
  Stemmed = 6,
  Fired = 7,
  Abandoned = 8,
  Misfire = 9,
}

export const HOLE_STATUS_LABELS: Record<HoleStatus, string> = {
  get [HoleStatus.Designed]() { return i18n.t("holeStatus.designed", { ns: "blastPattern" }); },
  get [HoleStatus.Drilled]() { return i18n.t("holeStatus.drilled", { ns: "blastPattern" }); },
  get [HoleStatus.RedrillRequired]() { return i18n.t("holeStatus.redrillRequired", { ns: "blastPattern" }); },
  get [HoleStatus.BackfillRequired]() { return i18n.t("holeStatus.backfillRequired", { ns: "blastPattern" }); },
  get [HoleStatus.Dipped]() { return i18n.t("holeStatus.dipped", { ns: "blastPattern" }); },
  get [HoleStatus.Charged]() { return i18n.t("holeStatus.charged", { ns: "blastPattern" }); },
  get [HoleStatus.Stemmed]() { return i18n.t("holeStatus.stemmed", { ns: "blastPattern" }); },
  get [HoleStatus.Fired]() { return i18n.t("holeStatus.fired", { ns: "blastPattern" }); },
  get [HoleStatus.Abandoned]() { return i18n.t("holeStatus.abandoned", { ns: "blastPattern" }); },
  get [HoleStatus.Misfire]() { return i18n.t("holeStatus.misfire", { ns: "blastPattern" }); },
};

export enum DrillOutcome {
  Complete = 0,
  Short = 1,
  Collapsed = 2,
  Blocked = 3,
  Abandoned = 4,
}

export const DRILL_OUTCOME_LABELS: Record<DrillOutcome, string> = {
  get [DrillOutcome.Complete]() { return i18n.t("drillOutcome.complete", { ns: "blastPattern" }); },
  get [DrillOutcome.Short]() { return i18n.t("drillOutcome.short", { ns: "blastPattern" }); },
  get [DrillOutcome.Collapsed]() { return i18n.t("drillOutcome.collapsed", { ns: "blastPattern" }); },
  get [DrillOutcome.Blocked]() { return i18n.t("drillOutcome.blocked", { ns: "blastPattern" }); },
  get [DrillOutcome.Abandoned]() { return i18n.t("drillOutcome.abandoned", { ns: "blastPattern" }); },
};

export enum DrillingEntryMode {
  Summary = 0,
  Hole = 1,
}

export enum DetectionSource {
  Manual = 0,
  GpsDerived = 1,
  VendorCode = 2,
  Imported = 3,
}

export enum DipCondition {
  Dry = 0,
  Wet = 1,
  Dewatered = 2,
  Collapsed = 3,
  Blocked = 4,
  Cavity = 5,
}

export enum DipAction {
  None = 0,
  Backfill = 1,
  CleanOut = 2,
  Redrill = 3,
  Abandon = 4,
}

export const DIP_CONDITION_LABELS: Record<DipCondition, string> = {
  get [DipCondition.Dry]() { return i18n.t("dipCondition.dry", { ns: "blastPattern" }); },
  get [DipCondition.Wet]() { return i18n.t("dipCondition.wet", { ns: "blastPattern" }); },
  get [DipCondition.Dewatered]() { return i18n.t("dipCondition.dewatered", { ns: "blastPattern" }); },
  get [DipCondition.Collapsed]() { return i18n.t("dipCondition.collapsed", { ns: "blastPattern" }); },
  get [DipCondition.Blocked]() { return i18n.t("dipCondition.blocked", { ns: "blastPattern" }); },
  get [DipCondition.Cavity]() { return i18n.t("dipCondition.cavity", { ns: "blastPattern" }); },
};

export const DIP_ACTION_LABELS: Record<DipAction, string> = {
  get [DipAction.None]() { return i18n.t("dipAction.none", { ns: "blastPattern" }); },
  get [DipAction.Backfill]() { return i18n.t("dipAction.backfill", { ns: "blastPattern" }); },
  get [DipAction.CleanOut]() { return i18n.t("dipAction.cleanOut", { ns: "blastPattern" }); },
  get [DipAction.Redrill]() { return i18n.t("dipAction.redrill", { ns: "blastPattern" }); },
  get [DipAction.Abandon]() { return i18n.t("dipAction.abandon", { ns: "blastPattern" }); },
};

export interface BlastHoleDTO {
  id: number;
  holeName: string;
  holeTypeId: number;
  holeType: string;
  designCollarE: number | null;
  designCollarN: number | null;
  designCollarRl: number | null;
  designDip: number | null;
  designAzimuth: number | null;
  designDepthM: number | null;
  designBitSizeId: number | null;
  designBitSize: string | null;
  designDelayMs: number | null;
  designStemmingM: number | null;
  planE: number | null;
  planN: number | null;
  status: HoleStatus;
  attempts: number;
  drilledDepthM: number | null;
  lastOutcome: DrillOutcome | null;
  lastDrilledAt: DateTime | null;
  dipDepthM: number | null;
  waterDepthM: number | null;
  dipCondition: DipCondition | null;
  dipAction: DipAction | null;
  dippedAt: DateTime | null;
  chargedKg: number;
  stemmedM: number | null;
  decks: number;
  isWet: boolean;
  rowVersion: string;
}

export interface PatternHolesDTO {
  patternBlastId: number;
  reference: string;
  patternStatus: PatternStatus;
  canEdit: boolean;
  depthToleranceM: number;
  holes: BlastHoleDTO[];
  sheets?: WorkSheetDTO[];
}

export enum WorkSheetKind {
  Drill = 0,
  Dip = 1,
  Charge = 2,
}

export interface WorkSheetDTO {
  id: number;
  kind: WorkSheetKind;
  name: string;
  issuedToPersonId: number | null;
  issuedTo: string | null;
  issuedAt: DateTime;
  closedAt: DateTime | null;
  holeIds: number[];
  rowVersion: string;
}

export interface IssueWorkSheetRequest {
  kind: WorkSheetKind;
  name: string | null;
  issuedToPersonId: number | null;
  holeIds: number[];
}

export interface DrilledHoleDTO {
  id: number;
  blastHoleId: number;
  holeName: string;
  depthM: number;
  manualDepthM: number | null;
  importedDepthM: number | null;
  designDepthM: number | null;
  outcome: DrillOutcome;
  waterEncountered: boolean;
  isRedrill: boolean;
  bitSizeId: number;
  bitSize: string;
  completedAt: DateTime | null;
  detectionSource: DetectionSource;
  accepted: boolean;
  rowVersion: string;
}

export interface DrillingSessionHolesDTO {
  drillingBlastId: number;
  patternBlastId: number;
  reference: string;
  entryMode: DrillingEntryMode;
  canChangeMode: boolean;
  attempts: DrilledHoleDTO[];
  holes: PatternHolesDTO;
}

export interface SaveDrilledHoleRequest {
  drillingBlastId: number;
  blastHoleId: number;
  depthM: number;
  outcome: DrillOutcome;
  waterEncountered: boolean;
  bitSizeId: number | null;
  completedAt: DateTime | null;
  acceptOutsideTolerance: boolean;
  rowVersion: string | null;
}

export interface DrilledHoleSaveResultDTO {
  saved: boolean;
  warnings: Warning[];
  attempt: DrilledHoleDTO | null;
}

export interface HoleDipDTO {
  id: number;
  blastHoleId: number;
  holeName: string;
  dippedAt: DateTime;
  dippedByPersonId: number | null;
  dippedBy: string | null;
  depthM: number;
  waterDepthM: number | null;
  condition: DipCondition;
  action: DipAction;
  backfillM: number | null;
  designDepthM: number | null;
  detectionSource: DetectionSource;
  rowVersion: string;
}

export interface SaveHoleDipRequest {
  blastHoleId: number;
  dippedAt: DateTime | null;
  dippedByPersonId: number | null;
  depthM: number;
  waterDepthM: number | null;
  condition: DipCondition;
  action: DipAction;
  backfillM: number | null;
  acceptOutsideTolerance: boolean;
  rowVersion: string | null;
}

export interface HoleDipSaveResultDTO {
  saved: boolean;
  warnings: Warning[];
  dip: HoleDipDTO | null;
}

export interface ChargeMaterialDTO {
  id: number;
  name: string;
  unit: MaterialUnit;
  storageClass: StorageClass;
}

export interface HoleChargeLineDTO {
  id: number;
  deckNo: number;
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  storageClass: StorageClass;
  designQuantity: number | null;
  actualQuantity: number;
  fromDepthM: number | null;
  toDepthM: number | null;
}

export interface HoleChargeDTO {
  blastHoleId: number;
  holeName: string;
  chargedAt: DateTime | null;
  lines: HoleChargeLineDTO[];
  explosiveKg: number;
  chargeLengthM: number | null;
  stemmingM: number | null;
  designChargeLengthM: number | null;
  designStemmingM: number | null;
  flags: Warning[];
  columnVersion: string;
}

export interface PatternChargingDTO {
  patternBlastId: number;
  holes: HoleChargeDTO[];
  materials: ChargeMaterialDTO[];
  depthToleranceM: number;
  chargeLengthToleranceM?: number;
  stemmingToleranceM?: number;
  dipMaxAgeHours: number;
}

export interface ChargeLineInput {
  deckNo: number;
  blastMaterialId: number;
  designQuantity: number | null;
  actualQuantity: number;
  fromDepthM: number | null;
  toDepthM: number | null;
}

export interface SaveHoleChargeRequest {
  chargedAt: DateTime | null;
  lines: ChargeLineInput[];
  acceptFlags: boolean;
  columnVersion: string | null;
}

export interface HoleChargeSaveResultDTO {
  saved: boolean;
  warnings: Warning[];
  charge: HoleChargeDTO | null;
}

export interface IredesImportResultDTO {
  added: number;
  merged: number;
  updated: number;
  skipped: number;
  warnings: Warning[];
  session: DrillingSessionHolesDTO;
}

export interface HoleDesignMappingDTO {
  name: string | null;
  fields: Record<string, string>;
  diameterUnit: string;
  dipFromVertical: boolean;
}

export interface HoleDesignPreviewDTO {
  headers: string[];
  sample: string[][];
  rows: number;
  fields: string[];
  suggested: HoleDesignMappingDTO;
  saved: HoleDesignMappingDTO[];
}

export interface HoleDesignImportResultDTO {
  added: number;
  updated: number;
  removed: number;
  keptWithHistory: number;
  warnings: Warning[];
  holes: PatternHolesDTO;
}

export const DESIGN_FIELD_LABELS: Record<string, string> = {
  get HoleName() { return i18n.t("designField.holeName", { ns: "blastPattern" }); },
  get HoleType() { return i18n.t("designField.holeType", { ns: "blastPattern" }); },
  get CollarE() { return i18n.t("designField.collarE", { ns: "blastPattern" }); },
  get CollarN() { return i18n.t("designField.collarN", { ns: "blastPattern" }); },
  get CollarRl() { return i18n.t("designField.collarRl", { ns: "blastPattern" }); },
  get ToeE() { return i18n.t("designField.toeE", { ns: "blastPattern" }); },
  get ToeN() { return i18n.t("designField.toeN", { ns: "blastPattern" }); },
  get ToeRl() { return i18n.t("designField.toeRl", { ns: "blastPattern" }); },
  get Dip() { return i18n.t("designField.dip", { ns: "blastPattern" }); },
  get Azimuth() { return i18n.t("designField.azimuth", { ns: "blastPattern" }); },
  get Length() { return i18n.t("designField.length", { ns: "blastPattern" }); },
  get Diameter() { return i18n.t("designField.diameter", { ns: "blastPattern" }); },
  get DelayMs() { return i18n.t("designField.delayMs", { ns: "blastPattern" }); },
  get StemmingM() { return i18n.t("designField.stemmingM", { ns: "blastPattern" }); },
};
